import axios, {
  AxiosInstance,
  AxiosResponse,
  CancelTokenSource,
  AxiosError,
} from 'axios'
import * as Sentry from '@sentry/browser'

import { ENV } from '../constants'
import * as auth from 'services/auth'
import logger from 'utils/logger'

let resources: Record<string, unknown> = {}

export const STATUS = {
  HTTP_200_OK: 200,
  HTTP_204_NO_CONTENT: 204,
  HTTP_401_UNAUTHORIZED: 401,
}

export const baseURL =
  ENV === 'production'
    ? 'https://api.rfpguide.com'
    : 'https://api.staging.rfpguide.com'

interface Headers {
  [key: string]: string
}

function redirectToLoginIfNeeded() {
  if (window.location.pathname.toLowerCase().startsWith('/carrier')) {
    return
  }
  if (window.location.pathname !== '/login') {
    window.location.href = `/login?next=${window.location.pathname}`
  }
}

const createHttpClient = (): AxiosInstance => {
  const headers: Headers = { 'Content-Type': 'application/json' }

  const token = auth.getToken()
  if (token) {
    headers['Authorization'] = `JWT ${token}`
  }

  const client = axios.create({
    headers,
    withCredentials: true,
    baseURL,
  })

  const isUnauthorized = (response: AxiosResponse | undefined): boolean =>
    response?.status === STATUS.HTTP_401_UNAUTHORIZED
  const isRefreshTokenURL = (url: string | undefined): boolean =>
    !!(url && url.search('token/refresh') >= 0)

  client.interceptors.response.use(
    (response: AxiosResponse) => {
      const { config } = response
      const method = config.method?.toLowerCase()

      if (method === 'post' || method === 'put' || method === 'delete') {
        resources = {}
      }

      return response
    },
    async (error: AxiosError) => {
      logger.error(`Axios error interceptor at ${error.config.url}`, {
        error: error as any,
        statusCode: error.response?.status,
        responseData: error.response?.data,
      })

      if (!isUnauthorized(error.response)) {
        return Promise.reject(error)
      }

      if (isRefreshTokenURL(error.config.url)) {
        auth.setToken('')
        auth.setRefreshToken('')
        redirectToLoginIfNeeded()
      }

      if (!auth.getRefreshToken()) {
        redirectToLoginIfNeeded()
      }

      try {
        const token = await auth.refreshToken()
        const config = error.config
        config.headers['Authorization'] = `JWT ${token}`
        return axios.request(config)
      } catch (error) {
        redirectToLoginIfNeeded()
      }
    }
  )

  return client
}

/**
 * makeRequestCreator: returns a fetcher that will handle cancelaiton
 * of on going requests and memoization, useful for search as you type
 * components. You should create a new request creator for every different
 * request, otherwise you will end up with tangled cancelation/memoization.
 */
export const makeRequestCreator = (axiosInstance: AxiosInstance) => {
  let cancel: CancelTokenSource

  return async (query: string) => {
    if (cancel) cancel.cancel()
    cancel = axios.CancelToken.source()

    try {
      if (resources[query]) return resources[query]
      const response = await axiosInstance(query, { cancelToken: cancel.token })
      const data = response?.data || {}
      resources[query] = data
      return data
    } catch (error) {
      if (!axios.isCancel(error)) {
        Sentry.captureException(error)
      }
    }
  }
}

export default createHttpClient()
